import Image from 'next/image';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Link from 'next/link';
import * as cartActions from 'aster-foundation/core/homecare/cart/redux/actions';
import * as wishListActions from 'aster-foundation/core/homecare/wishlist/redux/actions';
import dynamic from 'next/dynamic';
import { toast } from 'react-toastify';
import { getImageUrlWithSize } from '@common/Utility/common-utils';
import AddToCart from 'Homecare/Cart/AddToCart';
import { REDIRECT_URI } from 'aster-foundation/core/common/utils/ApiConst';
import {
  PRODUCT_CARD_DEFAULT,
  IC_DOT,
  IC_RX_HC,
  IC_WISHLIST_HC,
  IC_WATCH_HC,
  IC_BOOKMARK_CHECKED_BLUE_CIRCLE,
} from '../../Assets/images';
import {
  I_HAVE_PRESCRIPTION,
  PAYMENT_CURRENCY,
  PRESCRIPTION_MODAL_MESSAGE,
  REQUEST_CONSULTATION,
  RX_PRESCRIPTION_TITLE,
  SERVICE_ADDED_IN_CART,
  SERVICE_REMOVED_FROM_CART,
  TELECONSULT_ROUTE,
} from '@common/Utility/constants';
import { ServiceItemStyled } from './ServiceItemStyled';
import {
  getFormattedDate,
  convertStringtoTitleCase,
  getProductPathForHomeCare,
  timeConvert,
  getCartItemFromServiceObject,
  getFormattedDateValue,
} from '@common/Utility/common-utils';
import useGetItemEntryNo from 'Homecare/Hooks/UseGetItemEntryNo';
import Loader from '../Loader/Loader';
import {
  sendCartEvent,
  sendRxBottomSheet,
  sendSelectItemEvent,
  sendWhislistEvents,
} from 'Homecare/HomecareAnalytics';
import { isGuest, isPresentInCart } from 'Homecare/Cart/cartUtils';
import { useAuth } from '@common/AuthService';
import { flagsmith } from '@common/Lib/Flagsmith';
import { useNavigationURL } from '@common/Utility/generateNavigationURL';

const CustomModal = dynamic(() => import('../Modal/Modal'));

const ServiceItem = (props) => {
  const [charLimit, setCharLimit] = React.useState();
  const { authCtx } = useAuth();
  const dispatch = useDispatch();
  const router = useRouter();
  const generateURL = useNavigationURL();

  const appConfigData = useSelector((state) => state?.astercore?.AppConfig?.data?.data);
  const cartInfo = useSelector((state) => state?.homecare?.hcCartInfo?.cartInfo);
  const addToCartLoading = useSelector((state) => state?.homecare?.createCart?.isFetching);
  const deleteCartEntryLoading = useSelector((state) => state?.homecare?.deleteCartEntryNumber?.isFetching);
  const wishListSkuIds = useSelector((state) => state?.homecare?.getWishListHomecareSkuIDs?.data?.data);
  const userLogin = useSelector((state) => state?.astercore?.checkUserExist?.data?.entry[0]?.resource);

  const [currency, setCurrency] = React.useState(props?.currency ?? PAYMENT_CURRENCY);
  const [showModal, setShowModal] = React.useState(false);
  const [isInWishList, setIsInWishList] = React.useState(false);
  const [showLoginModal, setShowLoginModal] = React.useState(false);
  const [isServiceable] = React.useState(props?.earliestSlot && props?.isServiciable);

  const currencyAppConfig = appConfigData?.find((c) => c.name === 'currency')?.value;
  const pathName = getProductPathForHomeCare(props?.name, router?.query?.categories);
  const homecareFlagSmith = flagsmith.getValue('hc_app_homecare', { json: true });

  const entryNo = useGetItemEntryNo(props?.hisId);

  React.useEffect(() => {
    if (props?.currency) {
      setCurrency(props?.currency);
    } else if (currencyAppConfig) {
      setCurrency(currencyAppConfig);
    }
  }, [currencyAppConfig, props?.currency]);

  const redirectToLogin = () => {
    authCtx.login({
      redirectUri: `${REDIRECT_URI}${generateURL(`?asPath=${encodeURIComponent(router.asPath)}`)}`,
    });
  };

  const removeServiceFromMyList = () => {
    if (props?.item?.hisid) {
      const params = {
        itemID: props?.item?.hisid,
      };
      dispatch(
        wishListActions?.removeFromWishHomecareList({
          params,
          onSuccess: () => {
            setIsInWishList(false);
            if (props?.isPackage) {
              props?.toastService('PACKAGE_WISHLIST', 'REMOVED');
            } else {
              props?.toastService('SERVICE_WISHLIST', 'REMOVED');
            }
          },
          onFailure: () => {
            toast?.error('something_went_wrong');
          },
        }),
      );
    }
  };

  const addItemToCartApi = (param) => {
    sendCartEvent(props?.item, props?.isPackage, props?.pageName, props?.itemListId, props?.itemListName, true);
    const params = param;
    if (!isGuest()) {
      params.userId = userLogin?.id;
    }
    dispatch(
      cartActions.createCart({
        params,
        onSuccess: (response) => {
          localStorage.setItem('hcCartId', response?.cartId);
          sessionStorage.setItem('serviceAdded', 'true');
          props?.removeService && removeServiceFromMyList();
          if (props?.isPackage) {
            props?.toastService('PACKAGE', 'ADDED');
          } else {
            props?.toastService('SERVICE', 'ADDED');
          }
        },
        onFailure: () => {},
      }),
    );
  };

  const deleteItemFromCartApi = (param) => {
    sendCartEvent(props?.item, props?.isPackage, props?.pageName, props?.itemListId, props?.itemListName, false);
    const params = param;
    if (!isGuest()) {
      params.userId = userLogin?.id;
    }
    dispatch(
      cartActions.deleteCartEntryNumber({
        params,
        onSuccess: () => {
          if (props?.isPackage) {
            props?.toastService('PACKAGE', 'DELETED');
          } else {
            props?.toastService('SERVICE', 'DELETED');
          }
        },
        onFailure: () => {},
      }),
    );
  };

  const addToCartService = (e) => {
    e.stopPropagation();
    if (props?.isRxNeeded) {
      setShowModal(!showModal);
    } else if (!addToCartLoading && !isPresentInCart(props?.item?.hisid, cartInfo?.items)) {
      addItemToCartApi(getCartItemFromServiceObject(props?.item, cartInfo?.cartId, props?.price[0]?.salesPrice));
    }
  };
  const deleteFromCartService = (e) => {
    e.stopPropagation();
    deleteItemFromCartApi({ cartId: localStorage.getItem('hcCartId'), entryNo });
  };

  const havePrescription = () => {
    if (!addToCartLoading && !isPresentInCart(props?.item?.hisid, cartInfo?.items)) {
      addItemToCartApi(getCartItemFromServiceObject(props?.item, cartInfo?.cartId, props?.price[0]?.salesPrice));
      setShowModal(false);
    }
  };
  const bookConsultation = () => {
    router.push({ pathname: generateURL(`${TELECONSULT_ROUTE}/instantgp/details`) });
  };
  React.useEffect(() => {
    let charCount;
    if (window) {
      if (window.innerWidth < 980 && window.innerWidth > 767) {
        charCount = 41;
      } else if (window.innerWidth < 980) {
        charCount = 53;
      } else {
        charCount = 20;
      }
      setCharLimit(charCount);
    }
  }, []);

  React.useEffect(() => {
    if (wishListSkuIds) {
      setIsInWishList(wishListSkuIds?.find((item) => item === props?.item?.hisid));
    }
  }, [wishListSkuIds]);

  const LoginCheck = () => {
    if (!authCtx?.authenticated) {
      redirectToLogin();
      return false;
    }
    return true;
  };

  const removeFromWishlist = (e) => {
    e.stopPropagation();
    if (LoginCheck()) {
      sendWhislistEvents(props?.item, props?.isPackage, props?.pageName, props?.itemListId, props?.itemListName, false);
      const params = {
        itemID: props?.item?.hisid,
      };
      dispatch(
        wishListActions?.removeFromWishHomecareList({
          params,
          onSuccess: (response) => {
            setIsInWishList(false);
            if (props?.isPackage) {
              props?.toastService('PACKAGE_WISHLIST', 'REMOVED');
            } else {
              props?.toastService('SERVICE_WISHLIST', 'REMOVED');
            }
          },
          onFailure: () => {
            toast?.error('something_went_wrong');
          },
        }),
      );
    }
  };

  const addIntoWishlist = (e) => {
    e.stopPropagation();
    if (LoginCheck()) {
      sendWhislistEvents(props?.item, props?.isPackage, props?.pageName, props?.itemListId, props?.itemListName, true);
      const params = {
        itemID: props?.item?.hisid,
      };
      dispatch(
        wishListActions?.addToWishHomecareList({
          params,
          onSuccess: (response) => {
            setIsInWishList(true);
            if (props?.isPackage) {
              props?.toastService('PACKAGE_WISHLIST', 'ADDED');
            } else {
              props?.toastService('SERVICE_WISHLIST', 'ADDED');
            }
          },
          onFailure: () => {
            toast?.error('something_went_wrong');
          },
        }),
      );
    }
  };

  return (
    <ServiceItemStyled className={[props?.className, props?.isInServiceListingPage ? 'inServiceList' : ''].join(' ')}>
      {/* <Loader isLoading={addToCartLoading || deleteCartEntryLoading} /> */}
      <div className="dealItemBox cursor-pointer">
        <div className="item">
          <div className="addToWishList" onClick={(e) => (isInWishList ? removeFromWishlist(e) : addIntoWishlist(e))}>
            <Image
              src={isInWishList ? IC_BOOKMARK_CHECKED_BLUE_CIRCLE.src : IC_WISHLIST_HC.src}
              alt=""
              width={40}
              height={40}
            />
          </div>
          <Link href={generateURL(`${pathName}/${props?.hisId}`)} passHref>
            <a onClick={() => sendSelectItemEvent(props)}>
              <div className="imgWrapper">
                <div className="img" data-test="div_2">
                  <Image
                    src={getImageUrlWithSize(props?.serviceImage, 153, 182) || PRODUCT_CARD_DEFAULT.src}
                    alt={props?.name}
                    width="153px"
                    height="182px"
                    layout="fill"
                    unoptimized
                  />
                </div>
              </div>
              <div
                className={`name line-clamp ${!props?.isPackage && !props?.isRxNeeded ? 'mb-1' : ''}`}
                title={`${props?.name}`}
              >
                {props?.name}
              </div>
              <div className="package-rx-indicator">
                {props?.isPackage && <span className="package">Package</span>}
                {props?.isPackage && props?.isRxNeeded && (
                  <span className="dot">
                    <Image src={IC_DOT.src} alt={props?.name} width="8px" height="8px" />
                  </span>
                )}
                {props?.isRxNeeded && (
                  <span className="vector">
                    <Image src={IC_RX_HC.src} alt={props?.name} width="11px" height="11px" />
                  </span>
                )}
                {props?.isRxNeeded && <span className="required">Required</span>}
              </div>
              <div className="serviceTime pb-2">
                <span className="clock">
                  <Image src={IC_WATCH_HC.src} alt={props?.name} width="14px" height="14px" />
                </span>
                <span className="text">{timeConvert(props?.serviceTime)}</span>
              </div>
              {props?.earliestSlot?.date && props?.earliestSlot?.slot && (
                <div className={`earliestSlot ${!props?.isPackage && !props?.isRxNeeded ? 'custom-margin' : 'mb-4'}`}>
                  {' '}
                  <span className="text">{`Available ${getFormattedDateValue(
                    props?.earliestSlot?.date,
                  )}, ${convertStringtoTitleCase(props?.earliestSlot?.slot)}`}</span>
                </div>
              )}

              {props?.earliestSlot === null && (
                <div className="noSlot mb-4">
                  <span className="noSlotAvailable">No slots available </span>
                </div>
              )}
            </a>
          </Link>
          <div className="add-to-cart mt-auto">       
            <div className="price homeCareCart" style={{alignItems: 'flex-start'}}>
              { props?.categoryType === 'homeCare' && (
              <div className="serviceTime">
                <span className="textSecond">{homecareFlagSmith?.disclaimer?.startingFrom}</span>
              </div>
              )}
              {props?.price[0]?.salesPrice ? (
                <div className="new">
                  {currency} {props?.price[0]?.salesPrice}
                </div>
              ) : (
                ''
              )}
            </div>
            <AddToCart
              className="list-btn"
              itemHisId={props?.hisId}
              isServiceable={isServiceable}
              addToCartService={addToCartService}
              deleteFromCartService={deleteFromCartService}
              salePriceValue={props?.price[0]?.salesPrice !== undefined || null}
            />
          </div>
        </div>
      </div>
      {showModal && (
        <CustomModal
          bodyTextCenter
          footerBtnFullWidth
          modalTitle={RX_PRESCRIPTION_TITLE}
          size="md"
          footerAlignment="center"
          primaryButtonText={REQUEST_CONSULTATION}
          secondaryButtonText={I_HAVE_PRESCRIPTION}
          cancelButton
          primaryButtonClicked={() => {
            sendRxBottomSheet(props?.isPackage, props?.pageName, props?.itemListName, REQUEST_CONSULTATION);
            bookConsultation();
          }}
          seconderyButtonClcked={() => {
            sendRxBottomSheet(props?.isPackage, props?.pageName, props?.itemListName, I_HAVE_PRESCRIPTION);
            havePrescription();
          }}
          cancelButtonClicked={() => {
            sendRxBottomSheet(props?.isPackage, props?.pageName, props?.itemListName, 'Cancel');
            setShowModal(!showModal);
          }}
          dangerButtonOrder
          modalFrom="homeCareModal"
          outerCustomClass="homecare"
        >
          <p className="mb-0 font-17 homecare-modal-msg">{PRESCRIPTION_MODAL_MESSAGE}</p>
        </CustomModal>
      )}
      {showLoginModal && (
        <CustomModal
          bodyTextCenter
          outerCustomClass="homecareLoginModal"
          footerBtnFullWidth
          modalTitle="Login/Register"
          size="md"
          footerAlignment="center"
          primaryButtonText="Login/Register"
          secondaryButtonText="Cancel"
          primaryButtonClicked={() => redirectToLogin()}
          seconderyButtonClcked={() => setShowLoginModal(false)}
        >
          <p className="mb-0 font-17">Please Login/Register first to proceed</p>
        </CustomModal>
      )}
    </ServiceItemStyled>
  );
};

ServiceItem.propTypes = {
  serviceImage: PropTypes.string,
  name: PropTypes.string,
  currency: PropTypes.string,
  className: PropTypes.string,
  isInServiceListingPage: PropTypes.bool,
  earliestSlot: PropTypes.object,
  price: PropTypes.object,
  itemAdded: PropTypes.bool,
  isPackage: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  isRxNeeded: PropTypes.bool,
  serviceTime: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  hisId: PropTypes.string,
  item: PropTypes.object,
  key: PropTypes.string,
  toastService: PropTypes.func,
  pageName: PropTypes.string,
  itemListName: PropTypes.string,
  itemListId: PropTypes.string,
  removeService: PropTypes.bool,
  isServiciable:PropTypes.bool,
  categoryType: PropTypes.string,
};

ServiceItem.defaultProps = {
  serviceImage: '',
  name: '',
  currency: 'AED',
  className: '',
  isInServiceListingPage: false,
  earliestSlot: {},
  price: {},
  itemAdded: false,
  isPackage: false,
  isRxNeeded: false,
  serviceTime: '',
  item: {},
  key: '',
  toastService: () => {},
  removeService: false,
  isServiciable: true,
  categoryType: '',
  
};

export default ServiceItem;
